/* eslint-disable max-lines */
import {
    Button,
    Caption,
    Flex,
    pxToRem,
    TextInput,
    useStyles,
    useTheme,
} from '@archipro-design/aria';

import { z } from 'zod';

import type { FieldValues, SubmitHandler } from 'react-hook-form';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useFetcher } from 'react-router';

import { zodResolver } from '@hookform/resolvers/zod';
import { EMAIL_INPUT } from '~/modules/validation/util/form';
import type { ForgotPasswordAction } from '~/routes/remix-api.user.forgot-password';
import React, { useEffect } from 'react';
import * as S from './ForgotPasswordForm.style';
import { useLatest } from 'ahooks';
import { useToaster } from '~/modules/root/context/ToasterContext';
import type { LoginFlow } from '../../hook/use-login-flow';
import {
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from '@rocco/components/form';
import { Button as RoccoButton } from '@rocco/components/button';
import { Input as RoccoInput } from '@rocco/components/input';
import {
    getHdeFieldLabelClassName,
    getHdeInputClassName,
} from '~/modules/hde/components/registration/hde-option-styles';

const forgotPasswordValidator = z.object({
    email: EMAIL_INPUT,
});

type FormShape = z.infer<typeof forgotPasswordValidator>;

interface ForgotPasswordFormProps {
    flow: Extract<LoginFlow, 'forgot-password' | 'forgot-password-completed'>;
    onBack?: (e: React.SyntheticEvent<HTMLElement, Event>) => void;
    onSuccess?: () => void;
    onFailure?: () => void;
    /** HDE dark theme styling to match HDE registration forms */
    variant?: 'default' | 'hde';
}

const ForgotPasswordForm = (props: ForgotPasswordFormProps) => {
    const { onSuccess, onFailure, onBack, flow, variant = 'default' } = props;
    const isHde = variant === 'hde';

    const { css } = useStyles();
    const theme = useTheme();
    const { toast } = useToaster();

    const forgotFetcher = useFetcher<ForgotPasswordAction>();

    const methods = useForm<FormShape>({
        resolver: zodResolver(forgotPasswordValidator),
        mode: 'onBlur',
    });
    const { control, handleSubmit, getValues } = methods;

    const onSubmit: SubmitHandler<FieldValues> = (data) => {
        if (forgotFetcher.state !== 'idle') return;
        forgotFetcher.submit(data, {
            method: 'post',
            action: `/remix-api/user/forgot-password`,
        });
    };

    const memoisedSuccess = useLatest(onSuccess);
    const memoisedFailure = useLatest(onFailure);

    useEffect(() => {
        const success = forgotFetcher.data && 'success' in forgotFetcher.data;
        const error = forgotFetcher.data && 'error' in forgotFetcher.data;
        if (success && forgotFetcher.state === 'idle') {
            toast('A new reset link has been sent to your email');
            memoisedSuccess.current?.();
        }
        if (error && forgotFetcher.state === 'idle') {
            memoisedFailure.current?.();
        }
    }, [forgotFetcher, memoisedSuccess, memoisedFailure, toast]);

    if (isHde) {
        return (
            <FormProvider {...methods}>
                <div className="flex min-h-0 flex-1 flex-col gap-4 pb-20 pt-12 md:pt-28">
                    <p className="mb-0 text-sm leading-normal text-[#161616]/80">
                        {flow === 'forgot-password-completed'
                            ? `We sent an email to ${getValues('email')}. If this email is connected to an ArchiPro account, you'll be able to reset your password.`
                            : `Enter the email address associated with your account and we'll email you a link to reset your password.`}
                    </p>

                    {flow === 'forgot-password' && (
                        <forgotFetcher.Form
                            id="forgotPasswordForm"
                            onSubmit={handleSubmit(onSubmit)}
                            className="flex min-h-0 flex-1 flex-col gap-4"
                        >
                            <FormField
                                control={control}
                                name="email"
                                render={({ field, fieldState: { error } }) => (
                                    <FormItem>
                                        <FormLabel
                                            className={getHdeFieldLabelClassName()}
                                        >
                                            Email address
                                        </FormLabel>
                                        <FormControl>
                                            <RoccoInput
                                                {...field}
                                                type="email"
                                                placeholder="Enter email address."
                                                autoComplete="email"
                                                autoCapitalize="off"
                                                color={
                                                    error ? 'error' : 'default'
                                                }
                                                className={getHdeInputClassName()}
                                            />
                                        </FormControl>
                                        <FormMessage className="text-red-600" />
                                    </FormItem>
                                )}
                            />
                            <div className="absolute bottom-0 z-10 mt-auto flex w-full flex-col gap-4 bg-[#EAEAEA] px-12 pb-14 md:static md:mt-4 md:flex-row md:items-center md:justify-center md:gap-5 md:py-0">
                                <div className="flex justify-center gap-5 md:flex-row md:items-center">
                                    <RoccoButton
                                        type="button"
                                        size="larger"
                                        className="flex h-[2.875rem] w-[8.4375rem] items-center justify-center rounded-full border-0 bg-[#D4D4D4] font-medium text-[#161616] hover:bg-[#C4C4C4] hover:text-[#161616]"
                                        onClick={(e) => onBack?.(e)}
                                    >
                                        Back
                                    </RoccoButton>
                                    <RoccoButton
                                        type="submit"
                                        size="larger"
                                        className="flex h-[2.875rem] w-[12rem] items-center justify-center rounded-full border-0 bg-[#323232] font-medium text-white hover:bg-[#4A4A4A] hover:text-white"
                                        disabled={
                                            forgotFetcher.state !== 'idle'
                                        }
                                    >
                                        Send reset link
                                    </RoccoButton>
                                </div>
                            </div>
                        </forgotFetcher.Form>
                    )}

                    {flow === 'forgot-password-completed' && (
                        <div className="flex min-h-0 flex-1 flex-col gap-4">
                            <p className="text-sm text-[#161616]/80">
                                Didn't get the email? Try{' '}
                                <button
                                    type="button"
                                    className="cursor-pointer underline hover:text-[#161616]"
                                    onClick={() =>
                                        onSubmit(getValues() as FieldValues)
                                    }
                                >
                                    resend email
                                </button>{' '}
                                again.
                            </p>
                            <div className="absolute bottom-0 z-10 mt-auto flex w-full flex-col gap-4 bg-[#EAEAEA] px-12 pb-14 md:static md:mt-4 md:flex-row md:items-center md:justify-center md:gap-5 md:py-0">
                                <div className="flex justify-center gap-5 md:flex-row md:items-center">
                                    <RoccoButton
                                        type="button"
                                        size="larger"
                                        className="flex h-[2.875rem] w-[10rem] items-center justify-center rounded-full border-0 bg-[#323232] font-medium text-white hover:bg-[#4A4A4A] hover:text-white"
                                        onClick={(e) => onBack?.(e)}
                                    >
                                        Back to login
                                    </RoccoButton>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </FormProvider>
        );
    }

    return (
        <Flex column>
            <Caption
                variant="03-alt"
                design={{ marginBottom: pxToRem(16) }}
                variables={{
                    captionColor: theme.siteVariables.colors.charcoal['250'],
                }}
            >
                {flow === 'forgot-password-completed'
                    ? `We sent an email to ${getValues(
                          'email',
                      )}. If this email is connected to an ArchiPro account, you’ll be able to reset your password.`
                    : `Enter the email address associated with your account and we’ll email you a link to reset your password.
            `}
            </Caption>

            {flow === 'forgot-password' && (
                <forgotFetcher.Form
                    id="forgotPasswordForm"
                    onSubmit={handleSubmit(onSubmit)}
                >
                    <Controller<FormShape>
                        control={control}
                        name={'email'}
                        render={({ field, fieldState: { error } }) => (
                            <TextInput
                                state={error?.message ? 'error' : 'default'}
                                message={error?.message}
                                size="md"
                                input={{
                                    ...field,
                                    placeholder: 'Enter email address.',
                                    label: 'Email address',
                                    required: true,
                                    autoCapitalize: 'none',
                                }}
                            />
                        )}
                    />

                    <Button
                        design={{
                            marginTop: pxToRem(44),
                        }}
                        key="forgot-password-button"
                        type="submit"
                        fluid
                        size={18}
                        variables={{ buttonPadding: 16 }}
                        disabled={forgotFetcher.state !== 'idle'}
                    >
                        SEND RESET LINK
                    </Button>
                </forgotFetcher.Form>
            )}

            {flow === 'forgot-password-completed' && (
                <Flex column gap="space-16">
                    <Button
                        key="back-to-login-button"
                        fluid
                        size={18}
                        variables={{ buttonPadding: 16 }}
                        onClick={(e) => onBack?.(e)}
                    >
                        BACK TO LOGIN
                    </Button>
                    <span className={css(S.FormLink)}>
                        Didn’t get the email? Try
                        <Button
                            selected={true}
                            transparent={true}
                            variables={{ buttonPadding: 0 }}
                            onClick={() => {
                                onSubmit(getValues());
                            }}
                        >
                            resend email
                        </Button>
                        again.
                    </span>
                </Flex>
            )}
        </Flex>
    );
};

export default ForgotPasswordForm;
